import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SearchResourceName,
  SearchResult,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { mergeContactsFunctionError } from "../commons/mergeContacts"; // GAVION follow-up M4
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";
// GAVION: batch 1 lead RPCs
import { createLeadMethods } from "../../../gavion/leads/supabaseLeadMethods";
// GAVION: batch 7 unguessable storage object names
import { objectNameFor } from "../../../gavion/storage/objectName";
// GAVION: email methods (batch 5)
import { createSupabaseEmailMethods } from "../../../gavion/email/api/supabaseEmailMethods";
// GAVION: batch 7 privacy (Law 25) methods
import { createPrivacyMethods } from "../../../gavion/privacy/api/supabasePrivacyMethods";
// GAVION A10: Profile password change (no update_password edge function)
import { createSupabasePasswordMethods } from "../../../gavion/profile/passwordMethods";
import { createSupabasePipelineMethods } from "../../../gavion/settings/pipeline/pipelineMethods";
import { createSupabaseLeadStatusMethods } from "../../../gavion/settings/leadStatuses/leadStatusMethods";
import { createSupabaseCommissionMethods } from "../../../gavion/commissions/commissionMethods";
// GAVION: batch 6 integration RPCs and admin views
import {
  createSupabaseIntegrationMethods,
  withResourceViews,
} from "../../../gavion/integrations/api/supabaseIntegrationMethods";
// GAVION company-create: find-or-create and the autocomplete name search (69_gavion_company_create.sql)
import {
  createSupabaseCompanyCreateMethods,
  isCompanySearch,
  listCompaniesBySearch,
} from "../../../gavion/companies/companyCreate";
// GAVION deal amount visibility (72_): deals are read from deals_visible and written with select=id
import { withDealsAmountView } from "../../../gavion/deals/dealsAmountView";
import { applyAccentSearch } from "../../../gavion/search/accentSearch"; // GAVION search-accents (100_)

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = withDealsAmountView(getBaseDataProvider()); // GAVION deal amount visibility (72_)
  const companyCreate = createSupabaseCompanyCreateMethods(getSupabaseClient); // GAVION company-create

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      // GAVION company-create: the autocomplete searches names through public.search_companies (ids, no count, one
      // RLS check), then reads those few rows from companies_summary in that order
      if (resource === "companies" && isCompanySearch(params)) {
        return listCompaniesBySearch(
          companyCreate.searchCompanyIds,
          async (ids) =>
            (await baseDataProvider.getMany("companies_summary", { ids })).data,
          params,
        );
      }
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(errorDetails?.message || "Failed to create the user"),
          { code: errorDetails?.code, email: errorDetails?.email },
        );
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        secondary_emails,
        first_name,
        last_name,
        administrator,
        avatar,
        disabled,
      } = data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            secondary_emails,
            first_name,
            last_name,
            administrator,
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesUpdate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(
            errorDetails?.message || "Failed to update account manager",
          ),
          { code: errorDetails?.code, email: errorDetails?.email },
        );
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async globalSearch(
      query: string,
      resources: readonly SearchResourceName[],
      maxResults: number,
    ): Promise<SearchResult[]> {
      const { data, error } = await getSupabaseClient().rpc("global_search", {
        query,
        resources,
        max_results: maxResults,
      });
      if (error) {
        console.error("global_search.error", error);
        throw new Error("Failed to search");
      }
      return data ?? [];
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        // GAVION follow-up M4: a refused cross-lead merge keeps its code (calls_company_mismatch)
        throw await mergeContactsFunctionError(error);
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
    ...createLeadMethods(getSupabaseClient), // GAVION: batch 1 lead RPCs
    ...createPrivacyMethods(getSupabaseClient), // GAVION: batch 7 privacy (Law 25) methods
    // GAVION: email methods (batch 5)
    ...createSupabaseEmailMethods(),
    ...createSupabaseIntegrationMethods(getSupabaseClient), // GAVION: batch 6 integration RPCs
    ...createSupabasePasswordMethods(getSupabaseClient), // GAVION A10: in-place password change
    ...createSupabasePipelineMethods(getSupabaseClient), // GAVION custom pipeline (Paramètres → Pipeline)
    ...createSupabaseLeadStatusMethods(getSupabaseClient), // GAVION lead statuses (Paramètres → Statuts des leads)
    ...createSupabaseCommissionMethods(getSupabaseClient), // GAVION commissions (subscriptions, statements)
    findOrCreateCompany: companyCreate.findOrCreateCompany, // GAVION company-create
    // GAVION deal package (76_): « Mettre à jour au prix actuel » (public.deal_proposal_reprice); returns the new MRR
    async dealProposalReprice(dealId: Identifier): Promise<number> {
      const { data, error } = await getSupabaseClient().rpc(
        "deal_proposal_reprice",
        { p_deal_id: dealId },
      );
      if (error) throw new Error(error.message);
      return Number(data);
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyAccentSearch(params); // GAVION search-accents (100_)
    },
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyAccentSearch(params); // GAVION search-accents (100_)
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyAccentSearch(params); // GAVION search-accents (100_)
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyAccentSearch(params); // GAVION search-accents (100_)
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyAccentSearch(params); // GAVION search-accents (100_)
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  // GAVION: batch 6 admin views — api_keys/webhook_endpoints hold secrets (key hashes, signing secrets)
  // and are not readable by the browser; every read goes to the safe *_admin views.
  return withResourceViews(
    withLifecycleCallbacks(
      getDataProviderWithCustomMethods(),
      lifeCycleCallbacks,
    ),
  ) as CrmDataProvider;
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  // GAVION: unguessable object names (batch 7). The bucket is private; the UI signs `path` (useStorageSrc).
  const filePath = objectNameFor(file.name);
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
